import styles from './SettingsForm.module.scss';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DevTool } from '@hookform/devtools';
import { z } from 'zod';
import { useCallback, useContext, useMemo, useState } from 'react';
import { DrawerForm, ErrorResponse, SuccessNotificationContext, handleFieldErrors } from '@dmc/ui';
import ErrorMessage from '@cas/components/state-management/error-message/ErrorMessage';
import { VdsButton } from '@volt/volt-extended-components-react';
import Tabs from '@cas/components/tabs/tabs/Tabs';
import { useSettingsFormTabs } from './useSettingsFormTabs';
import {
  mapProvidersWithLayerName,
  mapProvidersWithUrl,
  UiSettingsModel,
  usePutUiSettingsMutation,
} from '@casops/store/apis/settingsApi';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppDispatch } from '@casops/store/hooks';
import { closeSidePanel } from '@casops/store/slices/globalSlice';
import { defaultSettings } from '@casops/store/slices/settingsSlice';
import { MapProviderType } from '@cas/types/settings';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ApiBaseQueryError } from '@casops/store/api';

type FormModel = UiSettingsModel;

// No map provider should ever need to zoom more than this.
const MAX_ZOOM = 25;
const MIN_ZOOM = 1;

interface SettingsFormProps {
  settings?: FormModel;
}

export function SettingsForm({ settings }: SettingsFormProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { showSuccessMessage } = useContext(SuccessNotificationContext);

  const [putMutationTrigger] = usePutUiSettingsMutation();

  // Merge default values with the API response.
  const initialValues = useMemo<FormModel>(() => {
    if (settings !== undefined) {
      return {
        ...defaultSettings,
        ...settings,
        map: {
          ...defaultSettings.map,
          ...settings.map,
        },
        localization: {
          ...defaultSettings.localization,
          ...settings.localization,
        },
      };
    }

    return defaultSettings;
  }, [settings]);

  const schema = useMemo(() => {
    const maxZoomOutOfRangeMessage = t('Validation.MaxZoomOutOfRange', {
      min: MIN_ZOOM,
      max: MAX_ZOOM,
    });

    const maxNativeZoomOutOfRangeMessage = t('Validation.MaxNativeZoomOutOfRange', {
      min: MIN_ZOOM,
      max: MAX_ZOOM,
    });

    return z
      .object({
        siteName: z.string().min(1, { message: t('Validation.SpecifySiteName') }),
        map: z.object({
          providerType: z.nativeEnum(MapProviderType),
          uri: z.string().url().optional().nullable(),
          layerName: z.string().optional().nullable(),
          maxZoom: z
            .number()
            .int({ message: t('Validation.ValueMustBeInteger') })
            .min(MIN_ZOOM, { message: maxZoomOutOfRangeMessage })
            .max(MAX_ZOOM, { message: maxZoomOutOfRangeMessage }),
          maxNativeZoom: z
            .number()
            .int({ message: t('Validation.ValueMustBeInteger') })
            .min(MIN_ZOOM, { message: maxNativeZoomOutOfRangeMessage })
            .max(MAX_ZOOM, { message: maxNativeZoomOutOfRangeMessage }),
        }),
        localization: z.object({
          timezone: z.string().min(1, { message: t('Validation.SpecifyTimezone') }),
          units: z.string().min(1, { message: t('Validation.SpecifyMeasurementUnit') }),
          dateTimeNumberFormat: z.string().min(1, { message: t('Validation.SpecifyLocaleFormat') }),
        }),
      })
      .superRefine(({ map: { providerType, layerName, uri } }, ctx) => {
        if (mapProvidersWithLayerName.includes(providerType)) {
          if (!layerName || layerName.trim() === '') {
            ctx.addIssue({
              code: 'custom',
              message: t('Validation.SpecifyMapLayerName'),
              path: ['map.layerName'],
            });
          }
        }

        if (mapProvidersWithUrl.includes(providerType)) {
          if (!uri || uri.trim() === '') {
            ctx.addIssue({
              code: 'custom',
              message: t('Validation.SpecifyUrl'),
              path: ['map.uri'],
            });
          }
        }
      });
  }, [t]);

  const {
    handleSubmit,
    setError,
    control,
    formState: { isSubmitting, dirtyFields, isValid },
    resetField,
  } = useForm<FormModel>({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  // Don't use the isDirty flag with ReactHookForm - see https://github.com/react-hook-form/react-hook-form/issues/3213.
  const isAnyFieldDirty = Object.keys(dirtyFields).length > 0;
  const canSubmit = !isSubmitting && isValid && isAnyFieldDirty;

  const onSubmitInternal = useCallback(
    async (data: FormModel) => {
      try {
        setErrorMessage(undefined);

        const payload: UiSettingsModel = { ...initialValues, ...data };
        await putMutationTrigger(payload).unwrap();

        // If we got here, it's successful because the methods above would have thrown
        showSuccessMessage(t(`SettingsSavedSuccessful`));

        dispatch(closeSidePanel());
      } catch (e: ApiBaseQueryError) {
        const errorResponse = new ErrorResponse(e);
        const validationError = errorResponse.getValidationError();

        if (validationError) {
          handleFieldErrors<FormModel>({
            serverErrorFields: { 'map.layerName': 'LayerName' },
            validationError,
            setError,
          });
        }

        setErrorMessage(t('Failed!') + ' ' + errorResponse.getTitle());
      }
    },
    [t, dispatch, setError, setErrorMessage, putMutationTrigger, showSuccessMessage, initialValues],
  );

  const { activeView, ...tabsProps } = useSettingsFormTabs({ control, resetField });

  const onSubmit = useCallback((): void => {
    handleSubmit(onSubmitInternal)();
  }, [handleSubmit, onSubmitInternal]);

  return (
    <>
      <DrawerForm
        footer={
          <VdsButton
            variant="primary"
            color="secondary-blue"
            isFullWidth
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {t('SaveChanges')}
          </VdsButton>
        }
      >
        <>
          {errorMessage !== undefined && <ErrorMessage message={errorMessage} />}

          <Tabs {...tabsProps} hasBorder />
          <div className={styles.fields}>{activeView}</div>
        </>
      </DrawerForm>

      <DevTool control={control} styles={{ panel: { height: '90vh' } }} placement="bottom-left" />
    </>
  );
}

export default SettingsForm;
